import { useEffect, useMemo, useState } from "react";
import { Copy, Check, ExternalLink, Code2, Info, Star, CalendarPlus, ShieldCheck, Plus, Trash2, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { PageHeader, SectionCard } from "@/components/common";
import { Switch } from "@/components/ui/switch";

const APP_ORIGIN = (typeof window !== "undefined" && window.location?.origin) || process.env.REACT_APP_BACKEND_URL || "";

const buildIframe = (url, { title, minHeight = 900 }) => (
  `<iframe src="${url}" title="${title}" style="width:100%; min-height:${minHeight}px; border:0; background:transparent;" loading="lazy" allow="clipboard-write; payment"></iframe>
<script>
  // Auto-resize the iframe to match the embedded page's height.
  window.addEventListener("message", function (e) {
    if (!e.data || e.data.type !== "pll-embed-height") return;
    var f = document.querySelector('iframe[src^="${APP_ORIGIN}"]');
    if (f && typeof e.data.height === "number") f.style.height = e.data.height + "px";
  });
<\/script>`
);

export default function AdminEmbeds() {
  const [includeReviews, setIncludeReviews] = useState(false);

  const reviewsUrl = `${APP_ORIGIN}/reviews`;
  const reviewsEmbedUrl = `${APP_ORIGIN}/embed/reviews`;
  const bookingBaseUrl = `${APP_ORIGIN}/book`;
  const bookingEmbedUrl = useMemo(() => {
    const params = new URLSearchParams({ embed: "1" });
    if (includeReviews) params.set("reviews", "1");
    return `${bookingBaseUrl}?${params.toString()}`;
  }, [bookingBaseUrl, includeReviews]);

  const reviewsIframe = buildIframe(reviewsEmbedUrl, { title: "Pit Lane Lounge — Reviews", minHeight: 720 });
  const bookingIframe = buildIframe(bookingEmbedUrl, { title: "Pit Lane Lounge — Book a Session", minHeight: 1100 });

  return (
    <div>
      <PageHeader
        title="Embeds"
        subtitle="Public URLs and copy-paste iframe snippets for the booking tool and reviews."
      />

      <SectionCard className="mb-6" data-testid="embeds-options">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-white font-display font-bold uppercase tracking-wider text-sm">Include reviews with the booking tool</p>
            <p className="text-slate-400 text-sm mt-1">Adds a published-reviews strip below the booking form in the embed snippet.</p>
          </div>
          <Switch
            checked={includeReviews}
            onCheckedChange={setIncludeReviews}
            data-testid="toggle-include-reviews"
          />
        </div>
      </SectionCard>

      <EmbedBlock
        title="Booking tool"
        subtitle="Guests can browse stations, pick a time, and pay via Stripe. Add ?embed=1 to hide our chrome."
        icon={CalendarPlus}
        publicUrl={bookingBaseUrl}
        embedUrl={bookingEmbedUrl}
        iframe={bookingIframe}
        testIdPrefix="booking"
      />

      <EmbedBlock
        title="Reviews"
        subtitle="A public gallery of published visitor reviews with aggregate stars."
        icon={Star}
        publicUrl={reviewsUrl}
        embedUrl={reviewsEmbedUrl}
        iframe={reviewsIframe}
        testIdPrefix="reviews"
        className="mt-6"
      />

      <AllowedDomains className="mt-6" />

      <SectionCard className="mt-6" data-testid="embed-help">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-[var(--neon-cyan)] shrink-0 mt-0.5" />
          <div className="space-y-2 text-slate-300 text-sm">
            <p><span className="text-white font-bold">Where to paste:</span> in WordPress use a Custom HTML block; on Squarespace / Wix / Webflow use the Embed / Code block.</p>
            <p><span className="text-white font-bold">Auto-resize:</span> the snippet includes a listener that resizes the iframe to match content, so users don&apos;t get a scrollbar inside.</p>
            <p><span className="text-white font-bold">Security:</span> add the site you&apos;re embedding on to the whitelist above so the browser allows the iframe.</p>
          </div>
        </div>
      </SectionCard>
    </div>
  );
}

function AllowedDomains({ className = "" }) {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [adding, setAdding] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  useEffect(() => {
    apiClient.get("/admin/embed-settings")
      .then(({ data }) => setDomains(data.allowed_domains || []))
      .catch((e) => toast.error(getErrorMessage(e, "Load failed")))
      .finally(() => setLoading(false));
  }, []);

  const addDomain = async () => {
    const v = input.trim();
    if (!v) return;
    setAdding(true);
    try {
      const { data } = await apiClient.post("/admin/embed-settings/domains", { domain: v });
      setDomains(data.allowed_domains);
      setInput("");
      setVerifyResult(null);
      toast.success(`Added ${data.added}`);
    } catch (err) {
      toast.error(getErrorMessage(err, "Add failed"));
    } finally {
      setAdding(false);
    }
  };

  const removeDomain = async (d) => {
    if (!window.confirm(`Remove ${d}?`)) return;
    try {
      const { data } = await apiClient.delete("/admin/embed-settings/domains", { params: { domain: d } });
      setDomains(data.allowed_domains);
      toast.success("Removed");
    } catch (err) {
      toast.error(getErrorMessage(err, "Remove failed"));
    }
  };

  const verify = async () => {
    const v = input.trim();
    if (!v) { toast.error("Enter a domain first"); return; }
    setVerifying(true);
    setVerifyResult(null);
    try {
      const { data } = await apiClient.post("/admin/embed-settings/verify", { domain: v });
      setVerifyResult(data);
    } catch (err) {
      setVerifyResult({ ok: false, error: getErrorMessage(err, "Verify failed") });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <SectionCard className={className} data-testid="allowed-domains">
      <div className="flex items-center gap-3 mb-1">
        <ShieldCheck className="w-5 h-5" style={{ color: "#00ff9d" }} />
        <h3 className="font-display text-lg font-bold text-white uppercase tracking-wider">Allowed embed domains</h3>
      </div>
      <p className="text-slate-400 text-sm mb-4">
        Browsers block iframes from unlisted origins. Add every site that will embed the booking tool or reviews.
        Wildcards like <code className="text-[var(--neon-cyan)]">https://*.example.com</code> are supported.
      </p>

      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <input
          type="text"
          value={input}
          onChange={(e) => { setInput(e.target.value); setVerifyResult(null); }}
          onKeyDown={(e) => { if (e.key === "Enter") addDomain(); }}
          placeholder="example.com  or  https://*.example.com"
          className="input-cyber flex-1"
          data-testid="domain-input"
        />
        <button onClick={verify} disabled={verifying || !input.trim()} className="neon-outline-btn px-4 py-2" data-testid="verify-domain">
          {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Verify
        </button>
        <button onClick={addDomain} disabled={adding || !input.trim()} className="neon-btn px-4 py-2" data-testid="add-domain">
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
        </button>
      </div>

      {verifyResult && (
        <div
          className="rounded-xl p-3 mb-3 flex items-start gap-2 text-sm"
          style={
            verifyResult.ok
              ? { border: "1px solid rgba(0,255,157,0.4)", background: "rgba(0,255,157,0.08)", color: "#00ff9d" }
              : { border: "1px solid rgba(255,90,90,0.45)", background: "rgba(255,90,90,0.08)", color: "#fca5a5" }
          }
          data-testid="verify-result"
        >
          {verifyResult.ok ? <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
          <div>
            {verifyResult.ok
              ? <>Domain is reachable (HTTP {verifyResult.status}). Safe to add.</>
              : <>Could not reach the domain{verifyResult.error ? `: ${verifyResult.error}` : "."} You can still add it — verification only checks reachability, not embed permissions.</>}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : domains.length === 0 ? (
        <div className="text-slate-500 text-sm border border-dashed border-slate-800 rounded-xl p-4 text-center">
          No custom domains yet — only this app itself can embed the pages.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="domain-list">
          {domains.map((d) => (
            <div key={d} className="flex items-center gap-2 rounded-lg pl-3 pr-1 py-1.5"
                 style={{ background: "rgba(0,229,255,0.08)", border: "1px solid rgba(0,229,255,0.35)" }}
                 data-testid={`domain-${d}`}>
              <ShieldCheck className="w-3.5 h-3.5" style={{ color: "var(--neon-cyan)" }} />
              <span className="text-[var(--neon-cyan)] font-mono text-xs">{d}</span>
              <button
                onClick={() => removeDomain(d)}
                className="w-6 h-6 grid place-items-center rounded-md hover:bg-red-500/20 transition-colors"
                data-testid={`remove-domain-${d}`}
                title={`Remove ${d}`}
              >
                <Trash2 className="w-3 h-3 text-red-300" />
              </button>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

function EmbedBlock({ title, subtitle, icon: Icon, publicUrl, embedUrl, iframe, testIdPrefix, className = "" }) {
  return (
    <SectionCard className={className} data-testid={`embed-${testIdPrefix}`}>
      <div className="flex items-center gap-3 mb-1">
        <Icon className="w-5 h-5" style={{ color: "var(--neon-cyan)" }} />
        <h3 className="font-display text-lg font-bold text-white uppercase tracking-wider">{title}</h3>
      </div>
      <p className="text-slate-400 text-sm mb-5">{subtitle}</p>

      <UrlRow label="Public URL" value={publicUrl} testId={`${testIdPrefix}-public-url`} openable />
      <UrlRow label="Embed URL" value={embedUrl} testId={`${testIdPrefix}-embed-url`} openable />

      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-slate-400 text-xs font-bold tracking-widest uppercase">
            <Code2 className="w-4 h-4" /> Copy-paste snippet
          </div>
          <CopyButton value={iframe} label="Copy snippet" testId={`${testIdPrefix}-copy-iframe`} />
        </div>
        <pre
          className="text-xs text-slate-300 p-4 rounded-xl overflow-x-auto whitespace-pre-wrap"
          style={{ background: "rgba(8,11,22,0.85)", border: "1px solid rgba(120,150,220,0.14)" }}
          data-testid={`${testIdPrefix}-iframe-code`}
        >
          {iframe}
        </pre>
      </div>
    </SectionCard>
  );
}

function UrlRow({ label, value, testId, openable = false }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-2">
      <span className="text-slate-500 text-xs font-bold tracking-widest uppercase w-32 shrink-0">{label}</span>
      <div className="flex-1 flex items-center gap-2 min-w-0">
        <input
          readOnly
          value={value}
          onFocus={(e) => e.currentTarget.select()}
          className="input-cyber py-2 text-xs flex-1 truncate"
          data-testid={testId}
        />
        {openable && (
          <a href={value} target="_blank" rel="noreferrer" className="neon-outline-btn px-3 py-2 text-xs" data-testid={`${testId}-open`}>
            <ExternalLink className="w-3.5 h-3.5" /> Open
          </a>
        )}
        <CopyButton value={value} label="Copy" testId={`${testId}-copy`} />
      </div>
    </div>
  );
}

function CopyButton({ value, label, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("Copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <button onClick={copy} className="neon-outline-btn px-3 py-2 text-xs shrink-0" data-testid={testId}>
      {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> {label}</>}
    </button>
  );
}
